import State from 'store/State';
import {
    buildUpdateWidgetConfigurationActionCreator,
    WidgetSetConfigAction,
    WidgetSetConfigPayload,
} from 'widgets/store/configuration/actions';
import { Reducer } from 'redux';
import { Action } from 'store/store';
import { WidgetType } from 'widgets/WidgetType';
import { WidgetWithConfigState } from 'widgets/store/configuration/WidgetWithConfigState';
import { createConfigurationReducer } from 'widgets/store/configuration/reducer';
import {
    useDispatchUpdateWidgetConfiguration,
    useToggleConfigOption,
    useWidgetConfigState,
} from 'widgets/store/configuration/hooks';
import { BooleanKeys } from 'utils/types/BooleanKeys';
import {
    buildConfigReducerWithGridSupport,
    BuildConfigReducerWithGridSupportOptions,
    ConfigStateWithGridSupport,
} from 'widgets/store/grid/reducer';

export interface ConfigSliceSelectors<TConfigState> {
    /*
     * Selects the configuration state for a widget
     */
    selectConfigState: (state: State, widgetKey: string) => TConfigState;
    /*
     * Hook to return the config state for a widget
     */
    useConfigSelector: () => TConfigState;
}

export interface ConfigSliceActions<TConfigState> {
    /*
     * Action creator for updating a widget configuration
     */
    createUpdateConfigAction: (
        widgetKey: string,
        configDelta: Partial<TConfigState>
    ) => WidgetSetConfigAction<TConfigState>;
}

export interface UseToggleConfigOptionRet {
    value: boolean;
    toggle: () => void;
}

export interface ConfigSliceHooks<TConfigState> {
    /*
     * Hook to dispatch an update widget config action
     */
    useDispatchUpdateConfigAction: () => (
        configDelta: Partial<TConfigState>
    ) => void;
    /*
     * Hook to toggle a config option
     */
    useToggleConfigOption: (
        option: BooleanKeys<TConfigState>
    ) => UseToggleConfigOptionRet;
}
/*
 * Slice of state for Widget Configuration
 */
export interface ConfigSlice<TConfigState> {
    selectors: ConfigSliceSelectors<TConfigState>;
    actions: ConfigSliceActions<TConfigState>;
    reducer: Reducer<
        TConfigState,
        Action<string, Partial<WidgetSetConfigPayload<TConfigState>>>
    >;
    hooks: ConfigSliceHooks<TConfigState>;
    initialConfigState: TConfigState;
}

/*
 * Build a config slice of state for a widget type
 */
export function buildConfigSlice<TConfigState>(
    widgetType: WidgetType,
    selectWidgetState: (
        state: State,
        widgetKey: string
    ) => WidgetWithConfigState<TConfigState>,
    initialConfigState: TConfigState,
    postReducer?: Reducer<
        TConfigState,
        Action<string, Partial<WidgetSetConfigPayload<TConfigState>>>
    >
): ConfigSlice<TConfigState> {
    // Selectors...

    const selectConfigState = (state: State, widgetKey: string) => {
        const widgetState = selectWidgetState(state, widgetKey);
        return widgetState && widgetState.config
            ? widgetState.config
            : { ...initialConfigState };
    };

    const useConfigSelector = () => useWidgetConfigState<TConfigState>();

    // Actions...

    const createUpdateConfigAction =
        buildUpdateWidgetConfigurationActionCreator<TConfigState>(widgetType);

    // Hooks...

    const useDispatchUpdateConfigAction = () =>
        useDispatchUpdateWidgetConfiguration<TConfigState>();

    const hooks: ConfigSliceHooks<TConfigState> = {
        useDispatchUpdateConfigAction,
        useToggleConfigOption: (option: BooleanKeys<TConfigState>) =>
            useToggleConfigOption<TConfigState>(option),
    };

    // Reducer...

    const reducer = createConfigurationReducer<TConfigState>(
        initialConfigState,
        postReducer
    );

    return {
        selectors: {
            selectConfigState,
            useConfigSelector,
        },
        actions: {
            createUpdateConfigAction,
        },
        reducer,
        hooks,
        initialConfigState,
    };
}

export function buildConfigSliceWithGridSupport<
    TColumnId extends string,
    TConfigState extends ConfigStateWithGridSupport<TColumnId>,
>(
    widgetType: WidgetType,
    selectWidgetState: (
        state: State,
        widgetKey: string
    ) => WidgetWithConfigState<TConfigState>,
    initialConfigState: TConfigState,
    options?: BuildConfigReducerWithGridSupportOptions<TColumnId, TConfigState>
): ConfigSlice<TConfigState> {
    const configSlice = buildConfigSlice(
        widgetType,
        selectWidgetState,
        initialConfigState
    );

    configSlice.reducer = buildConfigReducerWithGridSupport(
        widgetType,
        initialConfigState,
        options
    );

    return configSlice;
}

export type WidgetConfigSliceActionPayloadType<TConfigState> =
    WidgetSetConfigPayload<TConfigState>;

export type UseConfigSliceRet<TConfigState> = {
    config: TConfigState;
    useToggleConfigOption: (
        option: BooleanKeys<TConfigState>
    ) => UseToggleConfigOptionRet;
    dispatchConfigChanged: (configDelta: Partial<TConfigState>) => void;
};

export function useConfigSlice<TConfigState>(
    configSlice: ConfigSlice<TConfigState>
): UseConfigSliceRet<TConfigState> {
    const { selectors, hooks } = configSlice;
    return {
        config: selectors.useConfigSelector(),
        useToggleConfigOption: (option: BooleanKeys<TConfigState>) =>
            hooks.useToggleConfigOption(option),
        dispatchConfigChanged: hooks.useDispatchUpdateConfigAction(),
    };
}
